import React, { useState, useEffect, useRef } from "react";
import Modal from "../common/Modal";

const GameTypePickerModal = ({
  show,
  showGameTypePicker,
  closeGameTypePicker,
  onSelect,
  selectedKey,
  mode = "grid",
  gameTypes,
  title,
}) => {
  const visible = typeof show !== "undefined" ? show : showGameTypePicker;
  const [view, setView] = useState(() => {
    // Try to load from localStorage
    try {
      return localStorage.getItem("gameTypePickerView") || "grid";
    } catch {
      return "grid";
    }
  });

  const [filter, setFilter] = useState("");

  // Support gameTypes as an object (convert to array before filtering)
  let gameTypesArr = Array.isArray(gameTypes)
    ? gameTypes
    : Object.values(gameTypes || {});

  // Min/Max player dropdown filters (must be after gameTypesArr is defined)
  const minPlayersOptions = Array.from(new Set(gameTypesArr.map(g => g.minPlayers))).sort((a, b) => a - b);
  const maxPlayersOptions = Array.from(new Set(gameTypesArr.map(g => g.maxPlayers))).sort((a, b) => a - b);
  const [minPlayersFilter, setMinPlayersFilter] = useState("");
  const [maxPlayersFilter, setMaxPlayersFilter] = useState("");

  // Persist view preference
  const handleSetView = (v) => {
    setView(v);
    try {
      localStorage.setItem("gameTypePickerView", v);
    } catch {}
  };



  // Sort alphabetically by displayName
  gameTypesArr = gameTypesArr.slice().sort((a, b) => {
    const nameA = (a.displayName || '').toLowerCase();
    const nameB = (b.displayName || '').toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });


  // Player count filter logic
  function matchesPlayerCount(type) {
    if (minPlayersFilter && type.minPlayers !== Number(minPlayersFilter)) return false;
    if (maxPlayersFilter && type.maxPlayers !== Number(maxPlayersFilter)) return false;
    return true;
  }

  const filteredGameTypes = gameTypesArr.filter(
    (type) => (
      ((type.displayName && type.displayName.toLowerCase().includes(filter.toLowerCase())) ||
        (type.description && type.description.toLowerCase().includes(filter.toLowerCase())))
      && matchesPlayerCount(type)
    )
  );

  const listRef = useRef(null);

  // Auto-scroll the selected game into view (instant) when the modal is visible
  const _initialScrollDone = useRef(false);
  const _prevSelectedKey = useRef(null);
  useEffect(() => {
    if (!listRef.current || !selectedKey) return;

    // If modal is hidden, reset the refs so next open will auto-scroll again
    if (!visible) {
      _initialScrollDone.current = false;
      _prevSelectedKey.current = null;
      return;
    }

    // If we've already auto-scrolled to this selectedKey, don't do it again
    if (_initialScrollDone.current && _prevSelectedKey.current === selectedKey) return;

    // Support matching either the type.key or type.gameClass
    const selector = `[data-game-key="${selectedKey}"], [data-game-class="${selectedKey}"]`;
    const el = listRef.current.querySelector(selector);
    if (!el) return;

    const doScroll = () => {
      try {
        el.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
      } catch (e) {
        try { el.scrollIntoView(); } catch (e) {}
      }
      _initialScrollDone.current = true;
      _prevSelectedKey.current = selectedKey;
    };

    const raf = requestAnimationFrame(doScroll);
    return () => cancelAnimationFrame(raf);
  }, [visible, selectedKey]);

  if (!visible) return null;

  return (
    <Modal show={visible} title={title} onClose={closeGameTypePicker}>
  <div style={{ maxHeight: 'calc(90vh - 3cm)', display: 'flex', flexDirection: 'column' }}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl p-4 relative">
          <div className="mb-4 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="flex-1 px-3 py-2 rounded border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                placeholder={`Filter ${filteredGameTypes.length} games`}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <div className="flex items-center">
                <button
                  className={`mr-2 px-3 py-1 rounded  ${
                    view === "grid"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 dark:bg-gray-700 dark:text-gray-100"
                  }`}
                  onClick={() => handleSetView("grid")}
                  title="Grid view"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1zm10 0a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1zM4 15a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1zm10 0a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1z"/></svg>
                </button>
                <button
                  className={`px-3 py-1 rounded ${
                    view === "list"
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 dark:bg-gray-700 dark:text-gray-100"
                  }`}
                  onClick={() => handleSetView("list")}
                  title="List view"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5h8m-8 4h5m-5 6h8m-8 4h5M3 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zm0 10a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/></svg>
                </button>
              </div>
            </div>
            <div className="flex gap-4 mt-2 mb-1">
              <label className="flex items-center gap-1 text-xs">
                Min Players:
                <select
                  className="ml-1 px-1 py-0.5 rounded border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
                  value={minPlayersFilter}
                  onChange={e => setMinPlayersFilter(e.target.value)}
                >
                  <option value="">Any</option>
                  {minPlayersOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1 text-xs">
                Max Players:
                <select
                  className="ml-1 px-1 py-0.5 rounded border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
                  value={maxPlayersFilter}
                  onChange={e => setMaxPlayersFilter(e.target.value)}
                >
                  <option value="">Any</option>
                  {maxPlayersOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>
        <div
          ref={listRef}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl p-4 relative"
          style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}
        >
          {view === "grid" ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {filteredGameTypes.map((type) => (
                <div
                  key={type.gameClass}
                  data-game-key={type.key}
                  data-game-class={type.gameClass}
                  className={`border rounded-lg p-3 flex flex-col items-center cursor-pointer transition-colors duration-150 hover:shadow-lg hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    (selectedKey === type.key || selectedKey === type.gameClass)
                      ? "border-blue-500"
                      : "border-gray-300 dark:border-gray-600"
                  } bg-white dark:bg-gray-900`}
                  onClick={() => onSelect(type.gameClass)}
                >
                  <img
                    src={type.thumbnail}
                    alt={type.displayName}
                    className="w-16 h-16 object-contain mb-2"
                    style={{ background: "#f9f9f9", borderRadius: "8px" }}
                  />
                  <div className="font-semibold mb-1 text-gray-900 dark:text-gray-100 text-center">
                    {type.displayName}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300 text-center">
                    {type.description}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {type.minPlayers === type.maxPlayers
                      ? `${type.minPlayers} player${
                          type.minPlayers > 1 ? "s" : ""
                        }`
                      : `${type.minPlayers}-${type.maxPlayers} players`}
                  </div>
                </div>
              ))}
              {filteredGameTypes.length === 0 && (
                <div className="col-span-2 md:col-span-3 text-center text-gray-500 dark:text-gray-400 py-8">
                  No games found.
                </div>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredGameTypes.map((type) => (
                <li
                  key={type.gameClass}
                  data-game-key={type.key}
                  data-game-class={type.gameClass}
                  className={`py-6 px-4 cursor-pointer flex items-center justify-between transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    (selectedKey === type.key || selectedKey === type.gameClass)
                      ? "bg-blue-100 dark:bg-blue-900 hover:bg-blue-100 dark:hover:bg-blue-900"
                      : ""
                  }`}
                  onClick={() => onSelect(type.gameClass)}
                >
                  <div className="flex items-center">
                    <img
                      src={type.thumbnail}
                      alt={type.displayName}
                      className="w-16 h-16 object-contain rounded mr-4"
                      style={{ background: "#f9f9f9", borderRadius: "8px" }}
                    />
                    <div className="flex flex-col">
                      <span className="text-gray-900 dark:text-gray-100 font-semibold text-lg">
                        {type.displayName}
                      </span>
                      {type.description && (
                        <div className="text-xs text-gray-600 dark:text-gray-300 mt-1 max-w-lg">
                          {type.description}
                        </div>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                    {type.minPlayers === type.maxPlayers
                      ? `${type.minPlayers} player${
                          type.minPlayers > 1 ? "s" : ""
                        }`
                      : `${type.minPlayers}-${type.maxPlayers} players`}
                  </span>
                  
                </li>
              ))}
              {filteredGameTypes.length === 0 && (
                <li className="py-8 text-center text-gray-500 dark:text-gray-400">
                  No games found.
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default GameTypePickerModal;
